<template>
  <el-col
    :span="24"
    id="payments"
    v-loading.body="changingPage && !navigating"
    element-loading-text="Fetching page...">
    <el-row type="flex" justify="start" align="middle" id="payments-header">
      <HeaderInfoText :colspan="9" title="PAYMENTS" :meta="meta" :selection="selection"></HeaderInfoText>
      <el-col :span="6">
        <el-row type="flex" justify="center" v-if="meta.count > 20">
          <el-pagination
            layout="prev, pager, next, jumper"
            :total="meta.count"
            :page-size="20"
            @current-change="changePage"
            :current-page="currentPage"
          />
        </el-row>
      </el-col>
      <el-col :span="9" class="h-padded right-aligned-col">
        <el-button
          type="default"
          plain
          @click="printCopyMethod"
          :disabled="!printableApsSelected || printCopyDisable || !checkingVoid || !hasChangePermission"
          :loading="!!loadingRelated">
          {{  !!loadingRelated ? 'Loading' : 'Print Copy' }}
        </el-button>
        <el-select
            placeholder="Actions"
            v-model="voidUpdateSelection"
            style="padding: 0 6px;"
            @change="handleStatusSelection">
            <el-option label="Void Selected Payments" value='void' :disabled="!selection.length || !selectionIsVoidable || !hasChangePermission"></el-option>
            <el-option label="Delete Selected Payments" value='delete' :disabled="!selection.length || !selectionIsDeletable || !hasDeletePermission"></el-option>
            <el-option label="Send Email" value='sendEmail' :disabled="disableSentEmails || !hasChangePermission"></el-option>
        </el-select>
        <el-button plain @click="toggleFilters">
          {{ showFilters ? "Hide filters" : "Show filters" }}
        </el-button>
      </el-col>
    </el-row>
    
    <!-- flex-wrap:nowrap — element-plus el-row defaults to flex-wrap:wrap (element-ui did not), which
         pushed the full-width <Filters> onto its own line and left the vendor button in a parallel row
         above. Keep them on one line so the button sits inline as the first filter. -->
    <el-row type="flex" justify="start" align="middle" style="padding: 0 12px; flex-wrap: nowrap;">
      <el-button v-if="showFilters" class="me-4 vendor-button" @click="onClickVendors">Vendors Selected {{selectedVendors.length ? `(${selectedVendors.length})` : ''}}</el-button>
      <Filters
        :filters="filters"
        :filtersVisible="showFilters"
        :filterSize="4"
        clearAction="clearPaymentState"
        clearMetadataAction="resetPaymentMeta"
        searchAction="fetchPaymentsWithQuery"
        :autoApply="false"
        @doneFiltering="getAppliedFilters">
        <template v-for="(f, i) in filters" v-slot:[`${f.type}${i}`]="props" :key="i">
          <component :key="i" :is="f.type" :properties="f.props" :ref="`${f.props.filterName}_filter${f.props.suffix ? '_' + f.props.suffix : ''}`" />
        </template>
      </Filters>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col :span="24">
        <TransactionsConfPaymentList
          v-if="payments"
          name="payments_transactions_conf_list"
          ref="payments_conf_list"
          :aggridColumns="aggridColumns"
          :emitSelect="true"
          :rows="payments"
          :currentPage="currentPage"
          :baseQuery="baseQuery"
          @onSelect="handleSelectionChange"
          @handleVoidDialogVisible="handleVoidDialogVisible"
          @postPayment="postPayment"
          @filtersApplied="filtersApplied"
        />
        <el-row v-if="!receiving && !meta.count" align="middle" justify="center" class="nodata-heading">
          No data available
        </el-row>
      </el-col>
    </el-row>
    <el-dialog
    title="Report Viewer"
    width="90%"
    :show-close="true"
    :close-on-click-modal="true"
    :destroy-on-close="true"
    @close="handleClose"
    @before-close="handleBeforeClose"
    v-model:fullscreen="isFullscreen"
    top="5%"
    v-model="pdfLoader">
      <template v-if="loadPdf">
        <el-row type="flex" align="middle" >
          <object :data="`data:application/pdf;base64,${loadPdf}`" type="application/pdf"  width="1500" height="600"></object>
        </el-row>
      </template>
    </el-dialog>
    <el-dialog
      :title="modelTitle"
      :model-value="!!voidDialogVisible"
      width="90%"
      :show-close="false"
      :close-on-press-escape="false"
      :close-on-click-modal="false">
      <el-row type="flex" align="middle" justify="start" style="padding: 12px 0;">
        <span>
          {{
            `${typeof voidDialogVisible === 'object' && voidDialogVisible.id ? 'This payment is' : 'These payments are'}
            related to the APs listed below.`
          }}
        </span>
      </el-row>
      <el-row type="flex" align="middle" justify="center">
        <el-col :span="24">
          <el-table
            v-if="relatedTransactions.length"
            id="void-ap-table"
            :data="relatedTransactions"
            border
            style="width: 100%; z-index: 0;"
            ref="voidApTable">
            <el-table-column prop="ledger" label="Ledger ID" align="center" width="100"></el-table-column>
            <el-table-column prop="amount_decimal" label="Amount" align="center" width="120">
              <template v-slot="scope">
                ${{ scope.row.amount_decimal ? formatAmountLocale(scope.row.amount_decimal) : '0.00' }}
              </template>
            </el-table-column>
            <el-table-column prop="vendor" label="Vendor" align="left" fit>
              <template v-slot="scope">
                {{ getVendorName(scope.row.vendor) || ''}}
              </template>
            </el-table-column>
          </el-table>
        </el-col>
      </el-row>
      <el-row class="h-padded right-aligned-col">
        <el-col :span="24">
          <span style="padding: 12px 15px;"> Do you want to void associated AP{{(relatedTransactions && relatedTransactions.length) > 1 ? 's' : ''}}?</span><span><el-checkbox :disabled="!hasVoidApPermission" v-model="void_ap" /></span>
        </el-col>
      </el-row>
      <template v-slot:footer><span class="dialog-footer">
        <el-button
          class="exit-modal-btn"
          @click="voidDialogVisible = false">
          Cancel
        </el-button>
        <el-button
          class="exit-modal-btn"
          type="primary"
          @click="voidPayments">
          Confirm
        </el-button>
      </span></template>
    </el-dialog>
    <el-dialog
      title="Select Vendors"
      :model-value="!!vendorsDialogVisible"
      width="45%"
      :show-close="false"
      :close-on-press-escape="true"
      :close-on-click-modal="false">
      <el-row type="flex" align="middle" justify="center">
        <el-col>
          <div align="center">{{vendorLoading ? 'Loading Vendors...' : ''}}</div>
          <br>
          <el-transfer
            ref="accountTransfer"
            v-model="selectedVendors"
            :data="vendors"
            :titles="[ 'Unselected Vendors', 'Selected Vendors' ]"
            :props="{ key: 'id', label: 'display_name' }"
            filterable
            filter-placeholder="Search Vendor"
          >
          </el-transfer>
        </el-col>
      </el-row>
      <template v-slot:footer><span class="dialog-footer">
        <el-button
          class="exit-modal-btn"
          type="primary"
          @click="confirmVendors">
          Confirm
        </el-button>
      </span></template>
    </el-dialog>
    <el-dialog
      title=""
      v-model="sendEmailVisible"
      width="90%"
      :show-close="true"
      :close-on-press-escape="true"
      @close="closeSendEmailModal">
      <el-row type="flex" align="middle" justify="end" style="border-bottom: 1px solid lightgrey;">
      <HeaderInfoText :colspan="12" title="Send Email" />
      <el-col>
        <el-row type="flex" align="middle" justify="end">
          <el-button type="primary" @click="sendEmailVisible = false" style="margin: 0 15px;">Cancel</el-button>
          <el-button type="primary" @click="sendEmail" style="margin: 0 15px;">Send Email</el-button>
        </el-row>
      </el-col>
    </el-row>
      <nacha-payment-conf-list v-if="sendEmailVisible && selectedPayments" name="nacha_payment_conf_list" ref="nacha_payment_conf_list"
          :allow-edit="true" :allow-notify="step < 4" :width="width" :height="height" :row-data="selectedPayments" @onSelect="handleSelectedConf" @updateCCMail="handleCellUpdate" />
        <el-row v-if="!selectedPayments && !selectedPayments.length" align="middle" justify="center" class="nodata-heading">
          No data available
        </el-row>
    </el-dialog>
  </el-col>
</template>

<script>
  import { h } from 'vue'
  import { http } from '@/plugins/http'
  import { mapGetters } from 'vuex'
  import { payload } from '@/components/RouteComponents/Payments/actionPayload'
  import { payload as checksPayload } from '@/components/RouteComponents/Checks/actionPayload'
  import store from '@/store/store'
  import Filters from '@/components/Filters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import SelectFilter from '@/components/FilterComponents/SelectFilter'
  import DateFilter from '@/components/FilterComponents/DateFilter'
  import AutocompleteFilter from '@/components/FilterComponents/AutocompleteFilter'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import { notify } from '@/utils/helpers/Notify'
  import { formatLocale } from '@/utils/helpers/StringsNumbers'
  import { reportMixins } from '@/Mixins/Reports'
  import { sortObjectByProperty } from '@/utils/helpers/ObjectSorting'
  import TransactionsConfPaymentList from '@/components/SigmaClientNew/TransactionsConfPaymentList.vue'
  import NachaPaymentConfList from '@/components/SigmaClientNew/NachaPaymentConfList.vue'
  import { getHeaders, buildUrlWithQuery } from '@/utils/api'
  import { formatAmount } from '@/utils/helpers/AmountFormatter'
  import { retainFiltersOnEntry } from '@/Mixins/SigmaClientNew/transactionGridFilters'

  export default {
    components: {
      Filters,
      KeywordFilter,
      SelectFilter,
      DateFilter,
      AutocompleteFilter,
      HeaderInfoText,
      TransactionsConfPaymentList,
      NachaPaymentConfList
    },
    mixins: [ reportMixins ],
    data () {
      return {
        ccMail: {},
        selectedPayments: [],
        sendEmailVisible: false,
        tableHeight: 1000,
        showFilters: true,
        statusValues: [],
        selection: [],
        voidDialogVisible: false,
        relatedTransactions: [],
        changingPage: false,
        postingPayment: null,
        validationModel: null,
        voidingPayment: null,
        loadingRelated: false,
        currentPage: 1,
        voidUpdateSelection: null,
        vendorsDialogVisible: false,
        selectedVendors: [],
        vendors: [],
        vendorLoading: false,
        void_ap: false,
        aggridColumns: ['rowSelect', 'ledgerColumn', 'numberColumn', 'bankPaymentColumn', 'paymentTypeColumn', 'vendorColumn', 'dateRangeColumn', 'amountColumn', 'isNotifiedColumn', 'actionsColumn'],
        loadPdf: '',
        pdfLoader: false,
        isFullscreen: false, // v-model:fullscreen target for the Report Viewer dialog
        printCopyDisable: false
      }
    },
    methods: {
      getAppliedFilters () {
        this.currentPage = 1
        this.resetSelection()
        let extendedFilters = {}
        if (this.currentFilters.search) extendedFilters.search = this.currentFilters.search
        const filterModel = this.gridFilterModel()
        sessionStorage.setItem(`gridFilterModel:${this.$route.name}`, JSON.stringify({ model: filterModel, page: this.currentPage, extendedFilters: extendedFilters }))
        this.$store.dispatch('setFilter', { page: this.currentPage, ...this.currentFilters })
        this.$refs['payments_conf_list'].applyFiltersonGrid(filterModel, extendedFilters)
      },
      /**
       * The view's filters written as the models the columns' own filters read: the set filters hold
       * the values they offer - the bank by name, which is what its list is built from - and the
       * vendor and batch filters hold record ids alongside the names they show. ag-grid ticks a set
       * filter by walking the values as a list, so a bare id ticks nothing at all and the column is
       * left looking unfiltered while the rows behind it are not.
       * @return {Object}
       */
      gridFilterModel () {
        const asList = (value) => Array.isArray(value) ? value : (value === '' || value === null || value === undefined ? [] : [value])
        const named = (getter, ids) => ids
          .map(id => ({ id, name: (getter(id) || {}).name }))
          .filter(record => record.name)
        const namedVendor = (getter, ids) => ids
          .map(id => ({ id, name: (getter.find((obj) => obj.id === id) || {}).name }))
          .filter(record => record.name)
        const searchModel = (colId, records) => ({
          filterType: colId,
          values: records.map(record => record.id),
          names: records.map(record => record.name)
        })
        const model = {}

        const bankNames = named(this.getBankById, asList(this.currentFilters.batch__bank__in)).map(bank => bank.name)
        if (bankNames.length) model.bank = { filterType: 'set', values: bankNames }

        // The 'All' payment type is every one of them in a single value, offered here one by one
        const paymentTypes = asList(this.currentFilters.payment_type__in)
          .reduce((types, value) => types.concat(String(value).split(',')), [])
          .filter(Boolean)
        if (paymentTypes.length) model.payment_type = { filterType: 'set', values: paymentTypes }

        const statuses = asList(this.currentFilters.status__in)
          .reduce((types, value) => types.concat(String(value).split(',')), [])
          .filter(Boolean)
        if (statuses.length) model.status = { filterType: 'set', values: statuses }

        const vendors = namedVendor(this.vendors, asList(this.currentFilters.vendor__in))
        if (vendors.length) model.vendor = searchModel('vendor', vendors)

        const notifiedTypes = asList(this.currentFilters.is_notified__in)
          .reduce((types, value) => types.concat(String(value).split(',')), [])
          .filter(Boolean)
        if (notifiedTypes.length) model.is_notified = { filterType: 'set', values: notifiedTypes }

        if (this.currentFilters.date__gte || this.currentFilters.date__lte) {
          model.date = { filterType: 'date', type: 'inRange', dateFrom: null, dateTo: null }
          if (this.currentFilters.date__gte && this.currentFilters.date__lte) {
            model.date.dateFrom = this.currentFilters.date__gte
            model.date.dateTo = this.currentFilters.date__lte
          } else if (this.currentFilters.date__gte && !this.currentFilters.date__lte) {
            model.date.dateFrom = this.currentFilters.date__gte
          } else if (!this.currentFilters.date__gte && this.currentFilters.date__lte) {
            model.date.dateTo = this.currentFilters.date__lte
          }
        }

        return model
      },
      filtersApplied () {
        if (this.currentFilters['batch__bank__in']) {
          this.$refs.batch__bank_filter.selectedVal = this.currentFilters['batch__bank__in']
        } else {
          this.$refs.batch__bank_filter.selectedVal = []
        }
        if (this.currentFilters['payment_type__in']) {
          this.$refs.payment_type_filter.selectedVal = this.currentFilters['payment_type__in']
        } else {
          this.$refs.payment_type_filter.selectedVal = []
        }
        if (this.currentFilters['vendor__in']) {
          this.selectedVendors = this.currentFilters['vendor__in']
        } else {
          this.selectedVendors = []
        }
        if (this.currentFilters['is_notified__in']) {
          this.$refs.is_notified_filter.selectedVal = this.currentFilters['is_notified__in']
        } else {
          this.$refs.is_notified_filter.selectedVal = []
        }
        if (this.currentFilters.date__gte) {
          this.$refs.date_filter_gte.date = this.currentFilters.date__gte
        } else {
          this.$refs.date_filter_gte.date = ''
        }
        if (this.currentFilters.date__lte) {
          this.$refs.date_filter_lte.date = this.currentFilters.date__lte
        } else {
          this.$refs.date_filter_lte.date = ''
        }
      },
      formatAmountLocale (value) {
        return formatAmount(value)
      },
      closeSendEmailModal () {
        this.sendEmailVisible = false
      },
      resetSelection () {
        this.selection = []
      },
      handleBeforeClose (done) {
        this.loadPdf = null // Clear the PDF data
        done() // Close the dialog
      },
      // method to close the print close PDF.
      handleClose () {
        this.pdfLoader = false
      },
      // method is used to cal API to get Base64 data
      async printCopyMethod () {
        this.loadingRelated = true
        /* eslint-disable camelcase */
        let payment__in = []
        let accountpayable__in = []
        accountpayable__in = this.selection.filter(s => s.payment_type !== 'CHECK' && (s.related_transactions && s.related_transactions.length === 0)).map((ap) => ap.id)
        payment__in = this.selection.filter(s => s.payment_type !== 'CHECK' && (s.related_transactions && s.related_transactions.length > 0)).map((ap) => ap.id)
        const checks__in = this.selection.filter(s => s.payment_type === 'CHECK').map((ap) => ap.id)
        const newPayload = Object.assign({}, { payment__in, checks__in, accountpayable__in })
        const resp = await this.$store.dispatch('cacheParams', { data: newPayload })
        this.createCheckrunPdf(resp)
      },
      async createCheckrunPdf (resp) {
        try {
          const bank = this.getBankById(this.selection[0].bank.id)
          let payload = {}
          if (bank && bank.check_preferred_template) {
            let opts = await this.$store.dispatch('fetchOptions', { endpoint: 'reports/checks' })
            if (opts && opts.template_parameter && ['EFT', 'WIRE', 'CHECK', 'CHECK_MANUAL'].includes(this.selection[0].payment_type)) {
              if (this.selection[0].payment_type === 'EFT') {
                payload[opts.template_parameter] = 'payment_receipt_eft'
              } else if (this.selection[0].payment_type === 'WIRE') {
                payload[opts.template_parameter] = 'payment_receipt_wire_transfer'
              } else if (this.selection[0].payment_type === 'CHECK') {
                payload[opts.template_parameter] = 'default'
              } else if (this.selection[0].payment_type === 'CHECK_MANUAL') {
                payload.report_template = 'payment_receipt_manual_check'
              }
            } else {
              if (opts && opts.templates && opts.templates[bank.check_preferred_template]) {
                payload[opts.template_parameter] = bank.check_preferred_template
              }
            }
          } else {
            if (this.selection[0].payment_type === 'EFT') {
              payload.report_template = 'payment_receipt_eft'
            } else if (this.selection[0].payment_type === 'WIRE') {
              payload.report_template = 'payment_receipt_wire_transfer'
            } else if (this.selection[0].payment_type === 'CHECK') {
              payload.report_template = 'default'
            } else if (this.selection[0].payment_type === 'CHECK_MANUAL') {
              payload.report_template = 'payment_receipt_manual_check'
            }
          }
          payload.cached_parameters = resp.id
          const report = await this.$store.dispatch('buildPrintCopyrunPdf', payload)
          this.buildProgress = 100
          this.loadingRelated = false
          setTimeout(() => {
            this.loadPdf = report.data // Set the new PDF data
            this.pdfLoader = true // Open the dialog
          }, 100)
        } catch (e) {
          console.log(e)
          if (!(e instanceof DOMException)) {
            this.$notify({ title: 'Error', message: e.message || 'Error saving checks', type: 'error' })
          }
        }
      },
      /**
       * Post a payment
       * @param payment   The payment to be posted
       */
      async postPayment (payment) {
        try {
          const postPayload = payment.payment_type === 'CHECK' ? checksPayload : payload
          this.postingPayment = payment.id
          const newPayload = Object.assign({}, postPayload, { data: { status: 'POSTED' } }, { id: payment.id })
          const resp = await this.$store.dispatch('update', newPayload)
          if (resp && resp.id) {
            notify(this, 'success', 'Payment posted')
          } else {
            throw new Error()
          }
          return resp
        } catch (e) {
          console.log(e)
          notify(this, 'error', 'Error posting payment')
          return e
        } finally {
          this.postingPayment = null
          const { currentFilters } = this
          // issue 5: await the refetch so the grid's rows are repopulated deterministically instead of
          // racing an un-awaited dispatch (which left the grid transiently empty → "No rows to show").
          await this.$store.dispatch('fetchPaymentsWithQuery', currentFilters)
        }
      },
      /**
       * Handles saving a changed status value
       */
      saveChangedStatusValues () {
        this.$store.dispatch('savePc', this.statusValues)
      },
      async handleDeleteDialogVisible () {
        this.validationModel = this.$message({
          message: h('p', null, [
            h('span', null, 'Delete this Payment?'),
            h('button', { onClick: () => this.validationModel.close(), class: 'dialog-button' }, `Cancel`),
            h('button', { onClick: () => this.deletePayments(), class: 'dialog-button button-success' }, `Ok`)
          ]),
          type: 'info',
          duration: 0,
          showClose: true
        })
      },
      /**
       * Gets the related transactions for all payments to be voided. Shows/hides the void payment modal. If visible is
       * a payment object, gets the related transactions for just that payment (rather than all payments in this.selection)
       * and sets this.voidDialogVisible to the payment object so that it can be accessed after the user chooses to void that
       * single payment. Otherwise, voidDialogVisible is simply a boolean.
       * @param visible   Boolean or payment object
       */
      async handleVoidDialogVisible (visible) {
        if (!visible) {
          this.voidDialogVisible = false
          return
        }
        const { selection } = this
        let related
        if (typeof visible === 'object' && visible.related_transactions) {
          related = visible.related_transactions
          this.loadingRelated = visible.id
        } else {
          related = [...new Set([].concat(...selection.map(v => v.related_transactions)))]
          this.loadingRelated = true
        }
        try {
          if (Array.isArray(related) && related.length) {
            let promises = [this.$store.dispatch('fetchApsWithQuery', { query: { id__in: related }, storeLines: false })]
            if (related.length > 20) {                // fetch additional pages if aps > 20
              const reqsNeeded = Math.ceil(related.length / 20)
              for (let i = 2; i <= reqsNeeded; i++) {
                promises.push(this.$store.dispatch('fetchApsWithQuery', { query: { id__in: related, page: i }, storeLines: false }))
              }
            }
            const resp = await Promise.all(promises)
            if (resp && resp.length && resp.every(v => v.entities)) {
              this.relatedTransactions = resp.reduce((a, b) => a.concat(Object.values(b.entities.aps)), [])
            }
          }
        } catch (e) {
          console.log(e)
        } finally {
          this.loadingRelated = false
          this.voidDialogVisible = visible
        }
      },
      async deletePayments () {
        let selection = this.selection
        let promises = []
  
        const newPayload = Object.assign({}, payload, { payment_ids: selection.map((payment) => payment.id), ledger_ids: selection.map((payment) => payment.ledger) })
        promises.push(this.$store.dispatch('deletePayment', newPayload))

        this.validationModel.close()

        try {
          const resp = await Promise.all(promises)
          this.selection = []
          if (resp[0].status === 'success') {
            notify(this, 'success', 'Payments deleted')
          } else throw resp[0].body
        } catch (e) {
          console.log(e)
          notify(this, 'error', 'Error deleting payments')
        } finally {
          await this.$store.dispatch('clearPaymentState', true)
          const { currentFilters } = this
          this.$store.dispatch('fetchPaymentsWithQuery', currentFilters)
        }
      },
      /**
       * Handles voiding payments, then resets the selection and related transactions. If this.voidDialogVisible is a payment
       * object rather than a boolean, sets selection to just that payment, and assigns the payment id to this.voidingPayment.
       * Otherwise, selection is assigned the value of this.selection, which is an array of selected payments that should
       * be voided.
       */
      async voidPayments () {
        let selection
        if (typeof this.voidDialogVisible === 'object' && this.voidDialogVisible.id) {
          selection = [this.voidDialogVisible]
          this.voidingPayment = this.voidDialogVisible.id
        } else {
          selection = this.selection
        }
        this.handleVoidDialogVisible(false)
        const { relatedTransactions } = this
        if (!(selection.length && relatedTransactions.length)) return
        let promises = []
        selection.forEach(payment => {
          if (payment.id && payment.id > 0) {
            const newPayload = Object.assign({}, payload, { data: {payment_type: payment.payment_type, is_ap: this.void_ap} }, { id: payment.id })
            promises.push(promises.push(this.$store.dispatch('voidPayment', newPayload)))
          }
        })
        try {
          const resp = await Promise.all(promises)
          this.selection = []
          this.relatedTransactions = []
          if (resp.length === promises.length) {
            notify(this, 'success', (this.void_ap ? 'Payments and selected related transactions voided' : 'Payments voided'))
          }
        } catch (e) {
          console.log(e)
          notify(this, 'error', (this.void_ap ? 'Error voiding payments and related transactions' : 'Error voiding payments'))
        } finally {
          this.voidingPayment = null
          this.void_ap = false
          const { currentFilters } = this
          this.$store.dispatch('fetchPaymentsWithQuery', currentFilters)
        }
      },
      /**
       * Handles transaction selection
       * @param selection
       */
      handleSelectionChange (selection) {
        let notThisPage = this.selection.filter(v => v.page !== this.currentPage)
        this.selection = [...notThisPage, ...selection]
        const duplicatePaymentTypes = [...new Set(selection.map(s => s.payment_type))]
        if (selection && selection.length && duplicatePaymentTypes && duplicatePaymentTypes.length > 1) {
          this.printCopyDisable = true
          return notify(this, 'warning', 'Please select same Payment Type')
        } else {
          this.printCopyDisable = false
        }
      },
      /**
       * Preserves the selected transactions and shows them as selected whenever the table is re-rendered
       * due to a page or tab change
       */
      restoreSelection () {
        this.$refs['payments_conf_list'].restoreSelection(this.selection, 'ledger')
      },
      /**
       * Handles paginating between payment pages
       * @param page  Target page
       */
      async changePage (page) {
        this.changingPage = true
        const { currentFilters } = this
        currentFilters.page = page
        this.currentPage = page
        let newPayload = currentFilters && Object.keys(currentFilters).length
          ? Object.assign({}, payload, { page, query: currentFilters })
          : Object.assign({}, payload, { page })
        try {
          await this.$store.commit('UPDATE_CURRENT_PAYMENT_PAGE', page || 1)
          await this.$store.dispatch('clearLocalState', { clearMutation: payload.clearMutation, clearCache: true })
          newPayload.idAttribute = 'ledger'
          const entities = await this.$store.dispatch('fetchByPage', newPayload)
          let promises = await this.$store.dispatch('findDependencies', entities)
          await Promise.all(promises)
          let extendedFilters = {}
          if (currentFilters.search) extendedFilters.search = currentFilters.search
          const filterModel = this.gridFilterModel()
          sessionStorage.setItem(`gridFilterModel:${this.$route.name}`, JSON.stringify({ model: filterModel, page: page, extendedFilters: extendedFilters }))
          this.$store.dispatch('setFilter', { page: page, ...this.currentFilters })

          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          this.restoreSelection()
        } catch (e) {
          this.changingPage = false
          console.log(e)
        }
      },
      /**
       * Shows/hides filter pane
       */
      toggleFilters () {
        !this.showFilters ? this.showFilters = true : this.showFilters = false
      },
      /**
       * Formats dollar amounts with thousands separators
       * @param value
       * @return {*}
       */
      formatAmount (value) {
        return value ? formatLocale(value) : ''
      },

      async handleStatusSelection (e) {
        e === 'void' ? await this.handleVoidDialogVisible(true) : e === 'delete' ? await this.handleDeleteDialogVisible(true) : e === 'sendEmail' ? this.handleSelection(0) : ''
        this.voidUpdateSelection = null
      },
  
      async handleSelection (e) {
        if (e === 0) this.sendEmailVisible = true
        const instanceUrl = this.$store.getters.getActiveInstance.url
        const headers = getHeaders(this.$store.getters.getToken)

        await http.post(buildUrlWithQuery('get_vendors_by_payments', {}, instanceUrl), {payment_ids: this.selection.map((s) => (s.id))}, { headers })
          .then(response => {
            // Check results data is exist or not
            if (response && response.data && response.body.data) {
              this.selectedPayments = response.body.data.map(item => {
                return {
                  attributes: {
                    vendor_id: item.vendor_id,
                    vendor_name: item.vendor_name,
                    email: item.email,
                    routing: item.routing,
                    account: item.account,
                    amount_decimal: item.amount_decimal,
                    payment_confirmation_number: item.payment_confirmation_number,
                    resources: [{attributes: {company_email: item.company_email}}]
                  }
                }
              })
            }
          })
          .catch(error => console(error))
        return
      },

      async sendEmail () {
        try {
          if (!Object.values(this.ccMail).every(mail => mail.attributes.cc_mail)) {
            notify(this, 'error', 'A valid email address must be entered.')
            return
          }
          const payments = this.selection.filter(s => s.payment_type !== 'CHECK').map((ap) => ap.id)
          const checks = this.selection.filter(s => s.payment_type === 'CHECK').map((ap) => ap.id)
          let cc_mails = {}
          this.selectedPayments.forEach((mail) => {
            if (mail.attributes.cc_mail)cc_mails[mail.attributes.vendor_id] = mail.attributes.cc_mail
          })

          const newPayload = Object.assign({}, { payments, checks, cc_mail: cc_mails })
          const resp = await this.$store.dispatch('notifyPayment', newPayload)
          let offset = 0
          if (resp && resp.success.vendors.length) {
            notify(this, 'success', 'Email sent to ' + resp.success.vendors.map((vendor) => vendor.name).join(', '), offset)
          }
          if (resp && resp.error.vendors.length) {
            offset = 100
            notify(this, 'error', 'Unable to send email to ' + resp.error.vendors.map((vendor) => vendor.name).join(', '), offset)
          }
          return resp
        } catch (e) {
          console.log(e)
          notify(this, 'error', 'Error sent email')
          return e
        } finally {
          this.postingPayment = null
          const { currentFilters } = this
          this.$store.dispatch('fetchPaymentsWithQuery', currentFilters)
        }
      },

      handleSelectedConf (selection) {
        this.ccMail = selection
      },
  
      handleCellUpdate (param) {
        if (!param.newValue) {
          notify(this, 'error', 'A valid email address must be entered.')
          return
        }
        let index = this.ccMail.findIndex((mail) => mail.attributes.account === param.data.attributes.account)
        this.ccMail[index].attributes.cc_mail = param.newValue
      },
  
      onClickVendors () {
        this.vendorsDialogVisible = true
      },
      confirmVendors () {
        if (this.selectedVendors && this.selectedVendors.length) {
          this.$store.dispatch('setFilter', { 'vendor__in': this.selectedVendors })
        } else {
          this.$store.dispatch('unsetFilter', 'vendor__in')
        }
        this.vendorsDialogVisible = false
      },
      async allVendors () {
        this.vendorLoading = true
        let response = await this.getAllVendors()
        if (!response || !Array.isArray(response)) return false
        this.vendors = sortObjectByProperty(response, 'code').map((v) => { return { display_name: v.code + ' - ' + v.name, id: v.id, name: v.name } })
        this.vendorLoading = false
      },
      getVendorName (id) {
        if (this.vendors && this.vendors.length) {
          let vendor = this.vendors.find((vendor) => vendor.id === id)
          return vendor.display_name
        }
      }
    },
    computed: {
      ...mapGetters({
        meta: 'getPaymentMetadata',
        currentFilters: 'getFilters',
        getCheckrunPdfUrl: 'getCheckrunPdfUrl',
        receiving: 'getPaymentFetchStatus',
        getApById: 'getApById',
        getBankById: 'getBankById',
        getBatchById: 'getBatchById',
        getVendorById: 'getVendorById',
        getCompanyById: 'getCompanyById',
        getBankByBatchId: 'getBankByBatchId',
        navigating: 'getNavigationStatus',
        checkIfUserHasRequiredPermissions: 'checkIfUserHasRequiredPermissions'
      }),
      width () {
      // Reducing by 3 % to handle sigma-frontend navbar
        return '100%'
      },
      height () {
      // Reducing by 3 % to handle sigma-frontend navbar
        return window.innerHeight * 0.63 + 'px'
      },
      // disabling print copy option even if 'DRAFT', 'VOIDED', 'DELETED' is selected
      checkingVoid () {
        return this.selection.every(v => !['DRAFT', 'VOIDED', 'DELETED'].includes(v.status))
      },
      selectionIsVoidable () {
        return this.selection.every(v => !['DRAFT', 'VOIDED', 'DELETED'].includes(v.status) && !v.recon)
      },
      selectionIsDeletable () {
        return this.selection.every(v => ['DRAFT'].includes(v.status))
      },
      modelTitle () {
        return this.voidDialogVisible ? 'Void Associated APs' : 'Delete Associated APs'
      },
      payments () {
        return this.$store.getters.getAllCachedPayments
          .filter(v => v.page === this.currentPage)
          // .sort((a, b) => new Date(b.date) - new Date(a.date))
      },
      hasChangePermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_payment', 'entry.add_payment'])
      },
      hasDeletePermission () {
        return this.hasChangePermission && this.checkIfUserHasRequiredPermissions(['entry.delete_payment'])
      },
      hasExportAchPermission () {
        return this.hasChangePermission && this.checkIfUserHasRequiredPermissions(['entry.ach_payment'])
      },
      hasVoidApPermission () {
        return this.hasChangePermission && this.checkIfUserHasRequiredPermissions(['entry.post_accountspayable'])
      },
      baseQuery () {
        return { status__in: [ 'DRAFT', 'POSTED', 'VOIDED', 'CLOSED' ] }
      },
      filters () {
        return [
          {
            type: 'keywordFilter',
            props: {
              styles: '0 4px'
            }
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'batch__bank',
              clearable: true,
              opts: this.$store.getters.getAllBanks.map(bank => ({ label: bank.name, value: bank.id })),
              multi: true,
              placeholder: 'Bank',
              styles: '0 4px'
            }
          },
          {
            type: 'dateFilter',
            props: {
              filterName: 'date',
              placeholder: 'Payment Start',
              clearable: true,
              styles: '0 4px',
              suffix: 'gte'
            }
          },
          {
            type: 'dateFilter',
            props: {
              filterName: 'date',
              placeholder: 'Payment End',
              clearable: true,
              styles: '0 4px',
              suffix: 'lte'
            }
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'payment_type',
              clearable: true,
              opts: [
                { label: 'Check', value: 'CHECK' },
                { label: 'EFT', value: 'EFT' },
                { label: 'Manual Check', value: 'CHECK_MANUAL' },
                { label: 'Wire Transfer', value: 'WIRE' },
                { label: 'N/A', value: 'NA' }
              ],
              multi: true,
              placeholder: 'Payment Type',
              styles: '0 4px'
            }
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'is_notified',
              clearable: true,
              opts: [
                { label: 'Sent', value: 'True' },
                { label: 'Unsent', value: 'False' }
              ],
              multi: true,
              placeholder: 'Email Status',
              styles: '0 4px'
            }
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'status',
              clearable: true,
              opts: [
                { label: 'DRAFT', value: 'DRAFT' },
                { label: 'PRINTED', value: 'POSTED' },
                { label: 'VOIDED', value: 'VOIDED' },
                { label: 'CLOSED', value: 'CLOSED' },
                { label: 'DELETED', value: 'DELETED' }
              ],
              multi: true,
              defaults: [ 'DRAFT', 'POSTED', 'VOIDED', 'CLOSED' ],
              placeholder: 'Status',
              styles: '0 4px'
            }
          }
        ]
      },
      printableApsSelected () {
        return (!!this.selection.length)
      },
      disableSentEmails () {
        return !(this.selection && this.selection.length && !this.selection.filter((s) => ['CHECK', 'CHECK_MANUAL', 'WIRE'].includes(s.payment_type)).length)
      }
    },
    async beforeRouteEnter (to, from, next) {
      // Settled before the grid is built, so it only finds filters to put back on a return trip
      retainFiltersOnEntry(to.name, from.name)
      try {
        await store.dispatch('clearLocalState', { clearMutation: payload.clearMutation, clearCache: true })
        payload.idAttribute = 'index'
        const query = { status__in: [ 'DRAFT', 'POSTED', 'VOIDED', 'CLOSED' ] }
        await store.dispatch('setFilter', query)
        next()
      } catch (e) {
        console.log(e)
      }
    },
    mounted () {
      const tableHeight = () => {
        const parentHeight = document.getElementById('payments').offsetHeight
        const headerHeight = document.getElementById('payments-header').offsetHeight
        return parentHeight - headerHeight
      }
      this.tableHeight = tableHeight()
    },
    async created () {
      this.allVendors()
    },
    async beforeRouteLeave (to, from, next) {
      this.$store.commit('UPDATE_CURRENT_PAYMENT_PAGE', 1)
      try {
        await Promise.all([
          this.$store.dispatch('clearFilters'),
          this.$store.dispatch('clearApState'),
          this.$store.dispatch('clearPaymentState', true)
        ])
        sessionStorage.removeItem(`gridFilterModel:${this.$route.name}`)
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    }
  }
</script>

<style scoped>
  #payments-header {
    border-bottom: 1px solid lightgray;
  }
  .el-dialog--large {
    width: 90%;
    height: 150%;
}
  .h-padded {
    padding: 0 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  .el-transfer .el-transfer-panel {
    width: 315px !important;
  }
  .vendor-button {
    color: #bfcbd9;
    flex-shrink: 0;
    margin-right: 10px !important;
  }
  #payments .el-transfer-panel {
    width: 310px !important;
  }

</style>
<style>
#payments .el-transfer-panel {
    width: 310px !important;
}
/* element-plus renders el-select as a full-width block (element-ui's was inline/fixed-width), which
   stacked the header controls (Print Copy / Actions / Hide filters) vertically. Lay the header action
   column out inline and give the Actions select a fixed width to match the Vue-2 production layout. */
#payments-header .right-aligned-col {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: nowrap;
}
#payments-header .right-aligned-col .el-select {
  width: 160px;
  flex: 0 0 auto;
}
#payments-header .right-aligned-col .el-button {
  flex: 0 0 auto;
}
</style>
